import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { notifySlack } from "../utils/slackErrorNotifier";
import { getSession, clearSession, isSessionValid } from "../lib/crypto/session";
import { performHandshake } from "../lib/crypto/handshake";
import { encryptBody } from "../lib/crypto/encrypt";
import { decryptBody } from "../lib/crypto/decrypt";

// ─── Encryption toggle ────────────────────────────────────────────────────────
// ?bypassEncryption=true on the /login page disables encryption for the session.
// The value is stored in sessionStorage so it survives navigation away from /login.
const BYPASS_KEY = "bypassEncryption";

export function isEncryptionEnabled(): boolean {
  try {
    const isLoginPage = window.location.pathname.replace(/\/$/, "").endsWith("/login");
    if (isLoginPage) {
      const param = new URLSearchParams(window.location.search).get(BYPASS_KEY);
      if (param !== null) {
        sessionStorage.setItem(BYPASS_KEY, param);
        return param !== "true";
      }
    }
    const stored = sessionStorage.getItem(BYPASS_KEY);
    if (stored !== null) return stored !== "true";
  } catch {
    // no-op in non-browser envs (tests)
  }
  return process.env.REACT_APP_ENABLE_ENCRYPTION === "true";
}

// ─── Session mutex — prevents concurrent handshakes ───────────────────────────
let _handshakePromise: Promise<void> | null = null;

async function ensureSession(): Promise<void> {
  if (isSessionValid()) return;
  if (!_handshakePromise) {
    _handshakePromise = performHandshake().finally(() => {
      _handshakePromise = null;
    });
  }
  return _handshakePromise;
}

// ─── Axios instance ────────────────────────────────────────────────────────────
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const apiClient: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    secretkey: process.env.REACT_APP_API_SECRET_KEY,
  },
});

// ─── Request interceptor ───────────────────────────────────────────────────────
apiClient.interceptors.request.use(
  async (requestConfig) => {
    // Auth token
    const token = localStorage.getItem("token");
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }

    // Backward-compatible query param remap for summaries endpoint
    if (requestConfig.url?.includes("/summaries") && requestConfig.params) {
      const params = requestConfig.params as Record<string, any>;

      if (params.llmModel !== undefined && params.llm_model_id === undefined) {
        params.llm_model_id = params.llmModel;
        delete params.llmModel;
      }
      if (params.sourceType !== undefined && params.source_type === undefined) {
        params.source_type = params.sourceType;
        delete params.sourceType;
      }
      if (params.dateFrom !== undefined && params.start_date === undefined) {
        params.start_date = params.dateFrom;
        delete params.dateFrom;
      }
      if (params.dateTo !== undefined && params.end_date === undefined) {
        params.end_date = params.dateTo;
        delete params.dateTo;
      }
    }

    // Backward-compatible remap for legacy template update route:
    // PUT /email-templates/{id} -> PUT /update/email-templates
    if (
      requestConfig.method?.toLowerCase() === "put" &&
      typeof requestConfig.url === "string"
    ) {
      const templateUpdateMatch = requestConfig.url.match(
        /^\/email-templates\/([^/?#]+)$/
      );
      if (templateUpdateMatch) {
        const templateId = decodeURIComponent(templateUpdateMatch[1]);
        requestConfig.url = "/update/email-templates";
        const existingData =
          requestConfig.data && typeof requestConfig.data === "object"
            ? requestConfig.data
            : {};
        requestConfig.data = {
          ...existingData,
          email_template_id: Number.isFinite(Number(templateId))
            ? Number(templateId)
            : templateId,
        };
      }
    }

    // ── Encryption ────────────────────────────────────────────────────────────
    // Skip for FormData (file uploads) or when the caller sets _skipEncryption.
    const skipEncryption = (requestConfig as any)._skipEncryption === true;
    const isFormData = requestConfig.data instanceof FormData;

    if (isEncryptionEnabled() && !skipEncryption && !isFormData) {
      await ensureSession();
      const session = getSession();
      if (session) {
        requestConfig.headers["x-encryption"] = "true";
        requestConfig.headers["x-session-id"] = session.sessionId;
        // Private flag for the response interceptor — more reliable than re-reading headers.
        (requestConfig as any)._encrypted = true;

        if (requestConfig.data !== undefined && requestConfig.data !== null) {
          const encrypted = await encryptBody(requestConfig.data, session.aesKey);
          requestConfig.data = { data: encrypted };
        }
      }
    }

    return requestConfig;
  },
  (error) => {
    console.error("[API Request Error]", error);
    return Promise.reject(error);
  }
);

// ─── Response interceptor ─────────────────────────────────────────────────────
apiClient.interceptors.response.use(
  async (response: AxiosResponse) => {
    // Decrypt if the matching request was encrypted and the response body has
    // a `data` field that is a base64 string (the encrypted payload).
    // We replace only response.data.data with the decrypted value so that the
    // outer envelope (status, message, code …) is preserved for callers.
    if (
      isEncryptionEnabled() &&
      (response.config as any)._encrypted === true &&
      response.data !== null &&
      typeof response.data === "object" &&
      typeof response.data.data === "string"
    ) {
      const session = getSession();
      if (session) {
        try {
          const decrypted = await decryptBody(response.data.data, session.aesKey);
          response.data = { ...response.data, data: decrypted };
        } catch (err) {
          console.error("[Crypto] Response decryption failed", err);
        }
      }
    }

    return response;
  },
  async (error) => {
    console.error("[API Response Error]", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      data: error.response?.data,
    });

    // ── SESSION_REQUIRED: clear session, re-handshake, retry once ────────────
    if (
      isEncryptionEnabled() &&
      error.response?.status === 401 &&
      error.response?.data?.code === "SESSION_REQUIRED" &&
      !(error.config as any)?._retried
    ) {
      clearSession();
      try {
        await performHandshake();
        return apiClient({ ...error.config, _retried: true });
      } catch {
        // Handshake failed — fall through to standard 401 handling below.
      }
    }

    // ── Standard error handling ───────────────────────────────────────────────
    if (!error.response) {
      void notifySlack(
        new Error(error.message || "Network error"),
        {
          url: error.config?.url,
          method: error.config?.method,
          code: error.code,
        },
        "networkerror"
      );
    } else if (error.response?.status === 401) {
      console.warn("Unauthorized access - token may be expired");
    } else if (error.response?.status === 400) {
      void notifySlack(
        new Error(
          error.response?.data?.message || error.message || "Bad Request"
        ),
        {
          status: error.response?.status,
          code: error.response?.data?.code,
          apiMessage: error.response?.data?.message,
          url: error.config?.url,
          method: error.config?.method,
        },
        "api.handled"
      );
    } else if (error.response?.status === 403) {
      console.warn("Access forbidden - insufficient permissions");
    } else if (error.response?.status >= 500) {
      console.error("Server error occurred");
      void notifySlack(
        new Error(
          error.response?.data?.message || error.message || "Server error"
        ),
        {
          status: error.response?.status,
          code: error.response?.data?.code,
          apiMessage: error.response?.data?.message,
          url: error.config?.url,
          method: error.config?.method,
        },
        "api.handled"
      );
    }

    return Promise.reject(error);
  }
);

// ─── Generic API methods ───────────────────────────────────────────────────────
export class ApiService {
  static async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.get<T>(url, config);
    return response.data;
  }

  static async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.post<T>(url, data, config);
    return response.data;
  }

  static async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.put<T>(url, data, config);
    return response.data;
  }

  static async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.patch<T>(url, data, config);
    return response.data;
  }

  static async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.delete<T>(url, config);
    return response.data;
  }

  static async upload<T = any>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await apiClient.post<T>(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      },
    });

    return response.data;
  }
}

export default apiClient;
